import { type ChangeEvent, useRef, useState } from "react";

import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { MAX_FILE_SIZE } from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";

import { FILE_INPUT_ACCEPT } from "./utils/dropUtils";
import { buildFolderDropdownData } from "./utils/treeUtils";
import { FOLDER_INDENTATION_WIDTH, ROOT_FOLDER_VALUE } from "./constants";
import type { NodeMap } from "./types";

type AddDocumentModalProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  nodeMap: NodeMap;
  initialParentId: string | null;
  onSubmit: (file: File, parentId: string | null) => void;
};

export function AddDocumentModal({
  open,
  setOpen,
  nodeMap,
  initialParentId,
  onSubmit,
}: AddDocumentModalProps) {
  const [parentId, setParentId] = useState<string | null>(initialParentId);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleClose() {
    setOpen(false);
  }

  function handleSubmit() {
    if (!selectedFile) return;

    onSubmit(selectedFile, parentId);
    handleClose();
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setError(m.coInvestments_dataRoom_fileTooLarge());
      setSelectedFile(null);

      return;
    }

    setError(null);
    setSelectedFile(file);
  }

  const {
    depthMap,
    options: dropdownOptions,
    pathMap,
  } = buildFolderDropdownData(nodeMap);

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: handleSubmit,
        mainActionText: m.common_upload(),
        disabled: selectedFile === null,
        secondaryAction: handleClose,
        secondaryActionText: m.common_cancel(),
      }}
      dataTestId="add-document-modal"
      defaultWidth
      fluidHeight
      open={open}
      setOpen={(val) => {
        if (!val) handleClose();
      }}
      title={m.coInvestments_dataRoom_addDocument()}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="body-base-medium text-left text-text/primary">
            {m.coInvestments_dataRoom_file()}
          </span>
          <input
            accept={FILE_INPUT_ACCEPT}
            className="body-base-regular rounded-default border border-border/primary bg-background/primary px-3 py-2.5 text-text/primary file:mr-3 file:body-base-semibold file:border-0 file:bg-transparent file:p-0 file:text-text/primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-default"
            data-testid="add-document-file-input"
            onChange={handleFileChange}
            ref={fileInputRef}
            type="file"
          />
          {error !== null && (
            <ValidationErrorText dataTestId="add-document-error" withMargin>
              {error}
            </ValidationErrorText>
          )}
        </div>
        <ThemedDropdown
          isClearable={false}
          label={m.coInvestments_dataRoom_parentFolder()}
          name="add-document-parent"
          onChange={(opt) => {
            setParentId(
              opt === undefined || opt.value === ROOT_FOLDER_VALUE
                ? null
                : opt.value,
            );
          }}
          options={dropdownOptions}
          renderOption={(option) => {
            const depth = depthMap.get(option.value) ?? 0;

            return (
              <span
                style={{ paddingLeft: `${depth * FOLDER_INDENTATION_WIDTH}px` }}
              >
                {option.label}
              </span>
            );
          }}
          renderValue={() => {
            const key = parentId ?? ROOT_FOLDER_VALUE;
            const path = pathMap.get(key);

            return (
              <span className="min-w-0 truncate">
                {path || m.coInvestments_dataRoom_root()}
              </span>
            );
          }}
          value={parentId ?? ROOT_FOLDER_VALUE}
        />
      </div>
    </ActionaryModal>
  );
}
